import { skipToken } from "@reduxjs/toolkit/query";
import { checkPlaybookHasKnowledgeBaseAdherence } from "@repo/app-types/entities/playbook";
import {
  asRecordingId,
  type RecordingGradingAssessment,
} from "@repo/app-types/entities/recording";
import { isStaticTeammateAgentIdentity } from "@repo/app-types/entities/teammateAgent";
import type { FeedbackAccuracyData } from "@repo/app-types/helpers/feedbackHelpers";
import type { FC } from "react";
import { useMemo } from "react";
import { Header, Heading } from "react-aria-components";
import { useParams } from "react-router";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  useGetPlaybookTestResultQuery,
  useGetPlaybookTestSpecQuery,
} from "../../api/entities/playbookTestSpec.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { calculatePlaybookTestResultAccuracy } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import { FeedbackAccuracySummary } from "../../components/recordingAssessmentEditor/feedbackAccuracySummary.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { GradingAssessmentTable } from "../teammatePerformance/gradingAssessmentTable.js";
import { IncorrectStatementsIndication } from "../teammatePerformance/incorrectStatementsIndication.js";
import { KnowledgeBaseAdherenceStatementsCard } from "../teammatePerformance/knowledgeBaseAdherenceStatementsCard.js";
import { ToolUsageAccuracyCardContainer } from "../teammatePerformance/toolUsageAccuracyCardContainer.js";

/**
 * Page accessible via /agents/:teammateAgentId/testing/specs/:testSpecId/runs/:testRunId/results/:testResultId/assessment
 */
export const TeammateAgentResultAssessment: FC = () => {
  const { teammateAgentId, testSpecId, testResultId } = useParams();
  if (!teammateAgentId || !testSpecId || !testResultId) {
    throw new Error("Missing params.");
  }

  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;
  // Static agents have no playbook criteria/sections, so their tests skip
  // criteria/section grading; hide that (empty) assessment for them.
  const isStaticAgent =
    !!teammateAgent &&
    isStaticTeammateAgentIdentity(teammateAgent.teammate_agent_config.identity);

  const { data: playbookTestSpec } = useGetPlaybookTestSpecQuery(
    playbookId ? { playbookId, testSpecId } : skipToken,
  );

  const { data: playbookTestResult, isLoading: isTestResultLoading } =
    useGetPlaybookTestResultQuery(
      playbookId ? { playbookId, testSpecId, testResultId } : skipToken,
    );

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  // Transform the test result output into a format compatible with GradingAssessmentTable
  const assessment: RecordingGradingAssessment | null = useMemo(() => {
    if (!playbookTestResult?.output || !playbookTestSpec || !playbookId) {
      return null;
    }

    return {
      recording_assessment_template_id: playbookId,
      criteria_grading: playbookTestResult.output.criteria_grading,
      section_grading: playbookTestResult.output.section_grading,
      sales_performance: undefined,
    };
  }, [playbookTestResult, playbookTestSpec, playbookId]);

  // Calculate accuracy data from criteria and section grading
  const accuracyData: FeedbackAccuracyData | null = useMemo(() => {
    if (!playbookTestResult?.output || !playbookTestSpec || !playbook) {
      return null;
    }

    return calculatePlaybookTestResultAccuracy(
      playbookTestResult.output,
      playbookTestSpec.expected,
      playbook.assessment_tree,
    );
  }, [playbookTestResult, playbookTestSpec, playbook]);

  return (
    <>
      <div className="flex h-full">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="space-y-8 px-6 py-4">
            {assessment && playbook && playbookTestResult ? (
              <>
                {!isStaticAgent ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Test results
                      </Heading>
                      {accuracyData && !isTestResultLoading ? (
                        <FeedbackAccuracySummary accuracyData={accuracyData} />
                      ) : null}
                    </Header>

                    <GradingAssessmentTable
                      assessment={assessment}
                      // The test result ID is reused as a fake recording ID to satisfy the transcript component's interface.
                      recordingId={asRecordingId(playbookTestResult.id)}
                      playbook={playbook}
                      feedback={undefined}
                      disableNodeClicks
                      disableFeedback
                      expectedValues={playbookTestSpec?.expected?.nodes}
                    />
                  </section>
                ) : null}

                {checkPlaybookHasKnowledgeBaseAdherence(playbook) ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Knowledge base adherence
                      </Heading>

                      <IncorrectStatementsIndication
                        isVisibleWhenZero
                        knowledgeBaseAdherence={
                          playbookTestResult?.output?.knowledge_base_adherence
                        }
                      />
                    </Header>

                    <KnowledgeBaseAdherenceStatementsCard
                      knowledgeBaseAdherence={
                        playbookTestResult?.output?.knowledge_base_adherence
                      }
                    />
                  </section>
                ) : null}

                {playbookTestResult?.output?.tool_usage_accuracy ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Tool usage accuracy
                      </Heading>
                    </Header>

                    <ToolUsageAccuracyCardContainer
                      data={playbookTestResult.output.tool_usage_accuracy}
                    />
                  </section>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
      <NavigationSlideoverContainer />
    </>
  );
};
